/**
 * @file 文档页hooks
 * @author Wanzhongyun
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useLatest, useToggle } from 'react-use';
import { useHistory, useLocation } from 'react-router-dom';
import sleepPromise from 'sleep-promise';

import { getDocumentHTML } from 'src/api/pages/documentApi';
import useDocArticle, { getDocArticleFromHTML } from './components/doc-article/useDocArticle';
import useDocMenu from './components/doc-menu/useDocMenu';
import getTitleFromDom from 'src/utils/get-title-from-dom';
import getMenuItemFromDom from './components/doc-menu/components/getMenuItemFromDom';
import usePageLoading from 'src/components/page-loading/usePageLoading';
import useAnchorScrollHighlight from './components/doc-menu/components/doc-right-menu/useAnchorScrollHighlight';
import useVersionSelect from './components/version-select/useVersionSelect';
import { IDocumentParams } from './type';

const useDocument = (props: IDocumentParams) => {
    const { list, defaultVersionValue, isPage } = props;
    const history = useHistory();
    const location = useLocation();

    const docArticleHook = useDocArticle();
    const docMenuHooks = useDocMenu(isPage);
    const docStr = location.pathname.split('/').length > 0 ? location.pathname.split('/')[1] : 'lite';
    const versionSelectHook = useVersionSelect(docStr);
    const { setPageLoading } = usePageLoading(true);

    const [leftMenuCanShow] = useState<boolean>(true);

    const { setArticle } = docArticleHook;

    const { updateMenuList } = docMenuHooks;

    // 根据 hash 跳转到锚点位置
    const locationRef = useLatest(location);
    const scrollToAnchor = useCallback(() => {
        const anchorName = locationRef.current.hash.slice(1);
        if (!anchorName) {
            return;
        }
        const anchorDom = document.getElementById(anchorName) || document.querySelector(`a[name="${anchorName}"]`);
        if (anchorDom) {
            anchorDom.scrollIntoView({
                block: 'start'
            });
        }
    }, [locationRef]);

    // 根据url变化将浏览器滚动到顶部
    useEffect(() => {
        if (history.action === 'PUSH') {
            window.scrollTo({ top: 0 });
        }
    }, [history.action, docArticleHook.article]);

    useEffect(() => {
        scrollToAnchor();
    }, [location.hash, scrollToAnchor]);

    // 获取文档内容
    const theContextUrl = `${location.pathname}${location.search}`;

    useEffect(() => {
        (async () => {
            setPageLoading(true);
            const res = await getDocumentHTML(theContextUrl).catch(() => {
                // ignore
            });
            console.log('get document res');
            console.log(theContextUrl);
            console.log(res);
            if (res) {
                const docDom = (() => {
                    if (typeof res.text === 'string') {
                        const frag = document.createElement('html');
                        frag.innerHTML = res.text;
                        return frag;
                    }
                    return res.text;
                })();
                console.log(docDom);
                document.title = getTitleFromDom(docDom);

                const menuParams = getMenuItemFromDom(docDom);
                const article = getDocArticleFromHTML(docDom) || '';
                console.log(menuParams);
                setArticle(article);
                await sleepPromise(0);
                updateMenuList(menuParams.menuList, menuParams.selectedKeys);

                scrollToAnchor();
            }
            await sleepPromise(0);
            setPageLoading(false);
        })();
    }, [setPageLoading, scrollToAnchor, theContextUrl, setArticle, updateMenuList]);

    const [leftMenuShow, toggleLeftMenuShow] = useToggle(true);

    const docRightMenuList = docMenuHooks.right.menuList;
    const setDocRightSelectedKeys = docMenuHooks.right.setSelectedKeys;
    const setDocRightOpenKeys = docMenuHooks.right.setOpenKeys;
    const docArticleRef = useRef<HTMLDivElement>(null);
    // 页面滚动时高亮对应的锚点
    useAnchorScrollHighlight(
        ({ keys, parentKeys }) => {
            setDocRightSelectedKeys(keys);
            setDocRightOpenKeys([...parentKeys, ...keys]);
        },
        [docArticleRef, docRightMenuList]
    );

    // 获取版本选择器
    const setVersionOption = versionSelectHook.setOptions;
    const setVersionValue = versionSelectHook.setValue;
    const getCurrentVersion = versionSelectHook.getCurrentVersion;

    useEffect(() => {
        const currentList = list ?? [];
        setVersionValue(getCurrentVersion(currentList) || defaultVersionValue);
        setVersionOption(currentList);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [theContextUrl]);

    return {
        leftMenuCanShow,
        leftMenuShow,

        toggleLeftMenuShow,

        docArticleRef,

        docMenuHooks,
        docArticleHook,
        versionSelectHook
    };
};

export default useDocument;
